import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  IconButton,
  Typography,
  DialogContent,
  Stack,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

type SubtaskItemProps = {
  imgSrc: string;
  title: string;
  checked: boolean;
  id: number;
  onCheckedChange: (id: number) => void;
  imageHeight?: number;
  /** Stegets längd i minuter — sätts för rutinsteg, utelämnas för vanliga delsteg. */
  durationMinutes?: number;
};

const SubtaskItem: React.FC<SubtaskItemProps> = ({
  imgSrc,
  title,
  checked,
  onCheckedChange,
  imageHeight = 50,
  id,
  durationMinutes,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const maxWidth = isSmallScreen ? 'xs' : 'sm';

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [imageLoadError, setImageLoadError] = useState(false);

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleItemClick = () => {
    onCheckedChange(id);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: checked ? theme.palette.primary.main : 'transparent',
          borderRadius: theme.shape.borderRadius,
          my: 1,
          bgcolor: theme.palette.secondary.main,
          '&:hover': {
            bgcolor: theme.palette.secondary.dark,
          },
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          onClick={handleItemClick}
          sx={{
            padding: 1,
            cursor: 'pointer',
          }}
        >
          {}
          <Stack direction="row" alignItems="center">
            <Checkbox
              color="primary"
              checked={checked}
              onChange={handleItemClick}
              onClick={(e) => e.stopPropagation()}
              inputProps={{
                'aria-label': t('Mark title as checked', {
                  title: title,
                  status: checked ? 'incomplete' : 'complete',
                }) as string,
              }}
            />
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {title}
            </Typography>
            {/* Rutinsteg: handledaren behöver se HUR LÅNGT steget är, det är halva
                innehållet i en rutin. Vanliga delsteg har ingen tid och får ingen text. */}
            {durationMinutes !== undefined && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, whiteSpace: 'nowrap' }}
              >
                {t('routine.stepMinutesShort', { minutes: durationMinutes })}
              </Typography>
            )}
          </Stack>

          <Box width={imageHeight} height={imageHeight}>
            {imgSrc && imgSrc.length !== 0 && !imageLoadError && (
              <img
                src={imgSrc}
                alt={`${title}`}
                height={imageHeight}
                onError={() => setImageLoadError(true)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(e);
                }}
                style={{
                  cursor: 'pointer',
                  aspectRatio: '1/1',
                  borderRadius: '1em',
                  objectFit: 'cover',
                }}
              />
            )}
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth={maxWidth}
        fullWidth
        aria-labelledby="image-dialog-title"
      >
        <Box position="relative" overflow="hidden">
          <DialogContent>
            <img src={imgSrc} alt={title} id="image-dialog-title" style={{ width: '100%' }} />
          </DialogContent>
          <IconButton
            color="inherit"
            onClick={handleCloseDialog}
            aria-label="close"
            style={{ position: 'absolute', top: 0, right: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
};

export default SubtaskItem;
